import {
  bool,
  string,
  oneOf,
  oneOfType,
  func,
  number,
  any,
  object,
} from 'prop-types'
import { useEffect, useReducer, useRef } from 'react'
import { AnalyticsConsumer } from '@opallabs/quanta'
import { renderIcon, kuiperClassNames } from '../../../utils'
import InputLabel from '../../InputLabel'
import {
  asideClasses,
  buildThemes,
  highlightText,
  remainingLength,
} from './helpers'
import { actionTypes, reducer } from './reducer'

import '../styles.scss'

const TextInput = ({
  ariaLabel,
  aside,
  className,
  disabled,
  focusOnMount,
  highlight,
  highlightOnFocus,
  icon,
  iconColor,
  iconProps,
  inputType,
  isCurrency,
  maxLength,
  minValue,
  name,
  onBlur,
  onChange,
  onFocus,
  onKeyUp,
  placeholder,
  readOnly,
  resetOnBlur,
  shouldValidate,
  strict,
  style,
  theme,
  title,
  triggerOnKeyPress,
  truncate,
  type,
  valid,
  value,
  warningFactor,
}) => {
  const [state, dispatch] = useReducer(reducer, {
    cachedValue: value,
    isFocused: false,
  })

  const textInputRef = useRef(null)

  useEffect(() => {
    let focusTimer
    if (focusOnMount) {
      focusTimer = setTimeout(() => {
        textInputRef.current.focus()
      }, 500)
    }

    return () => {
      clearTimeout(focusTimer)
    }
  }, [focusOnMount])

  const focusInput = () => {
    if (disabled) return
    dispatch({ type: actionTypes.SET_FOCUS })

    if (onFocus) onFocus()
  }

  const blurInput = (e, track) => {
    const inputValue = e.target.value
    if (track) {
      track('blur', { value: inputValue, inputName: name })
    }
    const newValue = resetOnBlur && inputValue === '' ? value : inputValue
    if (onBlur) {
      onBlur(newValue)
    }
    dispatch({ type: actionTypes.SET_BLUR })
  }

  const onKeyDown = (e) => {
    const disable = ['e', '-', '+', '.']
    if (type === 'number' && disable.includes(e.key)) {
      e.preventDefault()
    }
  }

  const handleOnKeyUp = (e) => {
    if (triggerOnKeyPress && triggerOnKeyPress.keyCodes.includes(e.keyCode)) {
      e.preventDefault()
      triggerOnKeyPress.triggerFunction(e.keyCode)
      if (triggerOnKeyPress.blurOnTriggered) {
        e.target.blur()
      }
    }

    if (onKeyUp) onKeyUp(e)
  }

  const remaining = () => remainingLength(maxLength, value.length)

  const sizerValue = () => {
    if (!value || value === '') {
      return placeholder
    }
    return value
  }

  const inputElement = () => (
    <AnalyticsConsumer>
      {({ track }) => (
        <div className={kuiperClassNames('input-area')}>
          <input
            aria-label={ariaLabel}
            className={kuiperClassNames('input-element')}
            disabled={disabled}
            ref={textInputRef}
            type={type}
            onChange={(e) => onChange(e.target.value)}
            onFocus={(e) => {
              if (highlightOnFocus && !state.isFocused) {
                e.preventDefault()
                e.target.select()
              }
              focusInput()
            }}
            onBlur={(e) => blurInput(e, track)}
            onKeyUp={handleOnKeyUp}
            value={value || ''}
            maxLength={(strict && maxLength) || undefined}
            min={strict && minValue}
            style={style}
            readOnly={readOnly}
            onKeyDown={(event) => onKeyDown(event)}
            placeholder={placeholder}
          />
          <div className={kuiperClassNames('input-value-sizer')}>
            {sizerValue()}
          </div>
        </div>
      )}
    </AnalyticsConsumer>
  )

  const highlightableDiv = () => {
    if (maxLength && !truncate) {
      return highlightText(value, maxLength)
    }

    return null
  }

  const iconAriaMode = placeholder ? 'hidden' : 'presentational'

  return (
    /* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
    <div
      className={kuiperClassNames('input', className, buildThemes(theme), {
        [`color-${iconColor}`]: iconColor,
        [`type-${type}`]: type,
        [`input-${inputType}`]: inputType,
        currency: isCurrency,
        filled: value && value !== '',
        strict,
        focused: state.isFocused,
        disabled,
        highlight,
      })}
    >
      <InputLabel shouldValidate={shouldValidate} text={title} valid={valid} />

      <div
        className={kuiperClassNames('input-container', {
          'input-with-aside': aside,
        })}
        onClick={focusInput}
      >
        {icon &&
          renderIcon(icon, {
            ariaMode: iconAriaMode,
            ...(iconProps && { ...iconProps }),
          })}
        {inputElement()}

        {aside && maxLength && (
          <aside
            className={asideClasses(maxLength, value.length, warningFactor)}
          >
            {remaining()}
          </aside>
        )}
      </div>

      {highlightableDiv()}
    </div>
  )
}

TextInput.defaultProps = {
  ariaLabel: 'Text Input',
  aside: false,
  name: 'Text Input',
  className: 'textinput',
  shouldValidate: true,
  theme: 'dark',
  truncate: false,
  type: 'text',
  value: '',
  warningFactor: 0.2,
}

TextInput.propTypes = {
  ariaLabel: string,
  aside: bool,
  className: oneOfType([object, string]),
  disabled: bool,
  focusOnMount: bool,
  highlight: bool,
  highlightOnFocus: bool,
  icon: string,
  iconColor: string,
  iconProps: object,
  inputType: string,
  isCurrency: bool,
  maxLength: number,
  minValue: number,
  name: string,
  onBlur: func,
  onChange: func,
  onFocus: func,
  onKeyUp: func,
  placeholder: string,
  readOnly: bool,
  resetOnBlur: bool,
  shouldValidate: bool,
  strict: bool,
  style: object,
  theme: string,
  title: string,
  triggerOnKeyPress: oneOfType([object, bool]),
  truncate: bool,
  type: oneOf(['text', 'password', 'number']),
  valid: bool,
  value: any,
  warningFactor: number,
}

export default TextInput
